import React, { useMemo } from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { formatChartPeriodLabel, formatEuro, isFuturePeriod } from './statsUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

export default function StatsChart({ title, description, rows, groupBy, valueKey, color, money }) {
  const chartData = useMemo(() => {
    const futureRows = rows.map((row) => isFuturePeriod(row.period, groupBy));
    const datasets = [
      {
        label: title,
        data: rows.map((row, index) =>
          futureRows[index] ? null : Number(row[valueKey]) || 0
        ),
        borderColor: color,
        backgroundColor: alpha(color, 0.12),
        borderWidth: 2.5,
        fill: true,
        pointBackgroundColor: '#fff',
        pointBorderColor: color,
        pointBorderWidth: 2,
        pointRadius: rows.length <= 45 ? 3 : 0,
        pointHoverRadius: 5,
        tension: 0.32
      }
    ];

    if (futureRows.some(Boolean)) {
      datasets.push({
        label: `${title} — бъдещи периоди`,
        data: rows.map((_row, index) => (futureRows[index] ? 0 : null)),
        borderColor: '#aeb4c0',
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [5, 5],
        fill: false,
        pointBackgroundColor: '#d5d9e0',
        pointBorderColor: '#9299a6',
        pointBorderWidth: 1.5,
        pointRadius: rows.length <= 45 ? 3 : 0,
        pointHoverRadius: 5,
        tension: 0
      });
    }

    return {
      labels: rows.map((row) => formatChartPeriodLabel(row.period, groupBy)),
      datasets
    };
  }, [color, groupBy, rows, title, valueKey]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { intersect: false, mode: 'index' },
      plugins: {
        legend: { display: false },
        tooltip: {
          displayColors: false,
          callbacks: {
            label: (context) =>
              money ? formatEuro(context.parsed.y) : `${context.parsed.y} поръчки`
          }
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { autoSkip: true, maxTicksLimit: 12, maxRotation: 0 }
        },
        y: {
          beginAtZero: true,
          grid: { color: '#eef0f4' },
          ticks: {
            precision: money ? undefined : 0,
            callback: (value) => (money ? formatEuro(value, 0) : value)
          }
        }
      }
    }),
    [money]
  );

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: { xs: 2.5, md: 3 }, '&:last-child': { pb: { xs: 2.5, md: 3 } } }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
          <Box>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          </Box>
          <Box
            aria-hidden="true"
            sx={{ bgcolor: color, borderRadius: '50%', height: 10, mt: 1, width: 10 }}
          />
        </Stack>
        <Box sx={{ height: { xs: 260, md: 330 }, mt: 3 }}>
          <Line data={chartData} options={options} aria-label={title} role="img" />
        </Box>
      </CardContent>
    </Card>
  );
}
